import clsx from 'clsx';
import { useFiltersContext } from 'components/FiltersProvider/FiltersProvider';
import { CloseButton } from 'components/buttons/CloseButton/CloseButton';
import { useAppContext } from 'contexts/appContext';
import { useCallback } from 'react';
import { noScroll } from 'theme/base.css';
import { CounterInput } from './CounterInput/CounterInput';
import styles from './NumberOfGuests.css';
import {
	ADULTS,
	CHILDREN,
	DEFAULT_ANCHOR_GAP,
	DEFAULT_INITIAL_ADULTS,
	DEFAULT_INITIAL_CHILDREN,
	DEFAULT_MAX_TOTAL_GUESTS,
	DEFAULT_MIN_ADULTS,
	DEFAULT_MIN_CHILDREN,
} from './constants';
import { NumberOfGuestsProps, NumberOfGuestsSelection } from './types';

export const NumberOfGuests: React.FC<NumberOfGuestsProps> = ({
	testId,
	onCloseClick = () => {},
	selection = { adults: DEFAULT_INITIAL_ADULTS, children: DEFAULT_INITIAL_CHILDREN },
	minAdults = DEFAULT_MIN_ADULTS,
	minChildren = DEFAULT_MIN_CHILDREN,
	maxTotalGuests = DEFAULT_MAX_TOTAL_GUESTS,
	onChange = () => {},
	useBackdrop = false,
	onBackdropClick = () => {},
	anchorElement,
	anchorGap = DEFAULT_ANCHOR_GAP,
	rootClass = '',
	disableHorizontalPositioning = false,
	scrollingContainer = 'html',
	disableScrolling = true,
	showHeader = true,
}) => {
	const { dictionary } = useAppContext();
	const { updateSelection } = useFiltersContext();

	const { navNumberOfGuests, labelAdults, labelChildren, labelChildrenDisclaimer, labelNumberOfGuests } = dictionary;

	const { adults: adultsCount, children: childrenCount } = selection;

	const adultsPool = maxTotalGuests - childrenCount;
	const childrenPool = maxTotalGuests - adultsCount;

	const handleCountChange = useCallback(
		(selection: NumberOfGuestsSelection, type: string) => {
			onChange(selection);
			const value = type === ADULTS ? selection.adults : selection.children;
			updateSelection(type, { value: [value.toString()] });
		},
		[onChange, updateSelection],
	);

	const modalRefCallback = useCallback(
		(modalElement: HTMLDivElement | null) => {
			if (modalElement && anchorElement) {
				if (disableScrolling) {
					document.querySelector(scrollingContainer)?.classList.add(noScroll);
				}

				const { bottom, right } = anchorElement.getBoundingClientRect();
				const modalRefWidth = modalElement.offsetWidth;

				modalElement.style.top = `${bottom + anchorGap}px`;

				if (!disableHorizontalPositioning) {
					modalElement.style.left = `${right - modalRefWidth}px`;
				}
			} else if (disableScrolling) {
				document.querySelector(scrollingContainer)?.classList.remove(noScroll);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[anchorElement, anchorElement?.getBoundingClientRect(), anchorGap, disableHorizontalPositioning, scrollingContainer, disableScrolling],
	);

	return (
		<>
			<div className={clsx(styles.root, rootClass)} data-testid={testId} ref={modalRefCallback}>
				{showHeader && (
					<header className={styles.header}>
						<span className={styles.title}>{navNumberOfGuests}</span>
						<CloseButton testId={'number-of-guests-close-button'} onClick={onCloseClick} rootClass={styles.closeButton} />
					</header>
				)}
				<div className={styles.guests}>
					<div className={styles.guestContainer}>
						<div className={styles.labels}>
							<span className={styles.guestLabel}>{labelAdults}</span>
						</div>
						<CounterInput
							testId="adults"
							minCount={minAdults}
							maxCount={adultsPool}
							count={adultsCount}
							onCountChange={(adults) => {
								handleCountChange({ ...selection, adults }, ADULTS);
							}}
						/>
					</div>
					<div className={styles.guestContainer}>
						<div className={styles.labels}>
							<span className={styles.guestLabel}>{labelChildren}</span>
							<span className={styles.disclaimerLabel}>{labelChildrenDisclaimer}</span>
						</div>
						<CounterInput
							testId="children"
							minCount={minChildren}
							maxCount={childrenPool}
							count={childrenCount}
							onCountChange={(children) => {
								handleCountChange({ ...selection, children }, CHILDREN);
							}}
						/>
					</div>
				</div>
				<span className={styles.disclaimerLabel}>{labelNumberOfGuests}</span>
			</div>
			{useBackdrop && <div className={styles.backdrop} onClick={onBackdropClick} onKeyDown={onBackdropClick} />}
		</>
	);
};
